import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  ClipboardText, CircleNotch, FloppyDisk, CreditCard, Path, ListChecks,
  GraduationCap, Browser, CaretDown, CaretUp, WarningCircle,
  BookOpen, IdentificationCard, User, Baby, Barbell, ShieldCheck, CheckCircle, XCircle, Printer,
} from "@phosphor-icons/react";

const STATUT_STYLE = {
  a_revoir: "bg-zinc-700/40 text-zinc-300 border-zinc-600",
  en_test: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  fonctionnel: "bg-green-500/15 text-green-300 border-green-500/40",
  a_corriger: "bg-red-500/15 text-red-300 border-red-500/40",
  pret_rentree: "bg-emerald-500/20 text-emerald-300 border-emerald-500/50",
};
const PRIO_STYLE = {
  haute: "bg-red-500/15 text-red-400",
  moyenne: "bg-orange-500/15 text-orange-400",
  basse: "bg-zinc-600/30 text-zinc-400",
};
const CAT_ICON = { app: Browser, stripe: CreditCard, parcours: Path, priorite: ListChecks, formation: GraduationCap };
const CAT_ORDER = ["app", "stripe", "parcours", "priorite", "formation"];

// Rendu des champs propres à chaque catégorie (lecture seule)
function CategoryFields({ item }) {
  const f = item.fields || {};
  const Row = ({ k, v }) => (v ? (
    <div className="flex gap-2 text-xs">
      <span className="text-zinc-500 shrink-0 w-28">{k}</span>
      <span className="text-zinc-300">{Array.isArray(v) ? v.join(" → ") : v}</span>
    </div>
  ) : null);
  if (item.category === "app") return (
    <div className="space-y-1">
      <Row k="Rôle" v={f.role} /><Row k="Module / page" v={f.module} /><Row k="Type" v={f.type_page} />
      <Row k="Objectif" v={f.objectif} /><Row k="Point d'entrée" v={f.point_entree} /><Row k="Étapes clés" v={f.etapes} />
      <Row k="Action attendue" v={f.action_attendue} /><Row k="Page suivante" v={f.page_suivante} /><Row k="Confirmation" v={f.confirmation} />
    </div>
  );
  if (item.category === "stripe") return (
    <div className="space-y-1">
      <Row k="Type Stripe" v={f.type_stripe} /><Row k="Élément" v={f.element} /><Row k="Objectif" v={f.objectif} />
      <Row k="Déclencheur" v={f.declencheur} /><Row k="Vérification" v={f.verification} /><Row k="Résultat attendu" v={f.resultat} />
      <Row k="Lien app" v={f.lien_app} /><Row k="Webhook" v={f.webhook} />
    </div>
  );
  if (item.category === "parcours") return (
    <div className="space-y-1">
      <Row k="Parcours" v={f.parcours} /><Row k="Rôle" v={f.role} /><Row k="Étapes" v={f.etapes} /><Row k="Confirmation" v={f.confirmation} />
    </div>
  );
  if (item.category === "priorite") return (
    <div className="space-y-1">
      <Row k="Bloc" v={f.bloc} /><Row k="Élément" v={f.element} /><Row k="Pourquoi" v={f.pourquoi} />
      <Row k="Action" v={f.action} /><Row k="Impact si non réglé" v={f.impact} />
    </div>
  );
  if (item.category === "formation") return (
    <div className="space-y-1">
      <Row k="Rôle" v={f.role} /><Row k="Sujet" v={f.sujet} /><Row k="Objectif" v={f.objectif} />
      <Row k="Étapes" v={f.etapes} /><Row k="Format" v={f.format} /><Row k="Support" v={f.support} />
    </div>
  );
  return null;
}

function AuditCard({ item, statuts, priorites, onSaved }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({
    statut: item.statut, priorite: item.priorite || "",
    probleme_detecte: item.probleme_detecte || "", correction: item.correction || "",
    responsable: item.responsable || "", date_cible: item.date_cible || "", commentaires: item.commentaires || "",
  });
  const [saving, setSaving] = useState(false);
  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify({
    statut: item.statut, priorite: item.priorite || "",
    probleme_detecte: item.probleme_detecte || "", correction: item.correction || "",
    responsable: item.responsable || "", date_cible: item.date_cible || "", commentaires: item.commentaires || "",
  }), [draft, item]);

  const save = async (override = {}) => {
    setSaving(true);
    try {
      const payload = { ...draft, ...override };
      await api.patch(`/audit-rentree/items/${item.ref}`, payload);
      toast.success(`${item.ref} enregistré.`);
      onSaved(item.ref, payload);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec."); }
    finally { setSaving(false); }
  };

  const quickStatus = (v) => { setDraft((d) => ({ ...d, statut: v })); save({ statut: v }); };
  const ta = "w-full rounded-lg border border-zinc-800 bg-zinc-900 px-2.5 py-1.5 text-xs text-zinc-100 outline-none placeholder:text-zinc-600 resize-y";

  const [checked, setChecked] = useState(item.checked_steps || []);
  const parcoursSteps = item.category === "parcours" ? (item.fields?.etapes || []) : [];
  const toggleStep = async (idx) => {
    const next = checked.includes(idx) ? checked.filter((x) => x !== idx) : [...checked, idx];
    setChecked(next);
    try {
      await api.patch(`/audit-rentree/items/${item.ref}`, { checked_steps: next });
      onSaved(item.ref, { checked_steps: next });
    } catch { toast.error("Échec de l'enregistrement."); setChecked(checked); }
  };
  const stepsDone = parcoursSteps.length ? checked.filter((i) => i < parcoursSteps.length).length : 0;

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 overflow-hidden" data-testid={`audit-card-${item.ref}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <span className="text-[11px] font-mono text-zinc-500 shrink-0">{item.ref}</span>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-zinc-200 truncate font-medium">
            {item.fields?.module || item.fields?.element || item.fields?.parcours || item.fields?.sujet || item.ref}
          </div>
          <div className="text-[11px] text-zinc-500 truncate">{item.fields?.objectif || item.fields?.pourquoi || item.fields?.confirmation || ""}</div>
        </div>
        {parcoursSteps.length > 0 && (
          <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${stepsDone === parcoursSteps.length ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-800 text-zinc-400"}`} data-testid={`parcours-progress-${item.ref}`}>
            {stepsDone}/{parcoursSteps.length} étapes
          </span>
        )}
        {item.priorite && <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${PRIO_STYLE[item.priorite]}`}>{priorites[item.priorite]}</span>}
        <select data-testid={`audit-status-${item.ref}`} value={draft.statut} onChange={(e) => quickStatus(e.target.value)}
          className={`shrink-0 rounded-lg border px-2 py-1 text-[11px] font-medium outline-none ${STATUT_STYLE[draft.statut]}`}>
          {Object.entries(statuts).map(([k, v]) => <option key={k} value={k} className="bg-zinc-900 text-zinc-100">{v}</option>)}
        </select>
        <button onClick={() => setOpen((o) => !o)} className="text-zinc-500 hover:text-zinc-300 shrink-0" data-testid={`audit-toggle-${item.ref}`}>
          {open ? <CaretUp size={18} /> : <CaretDown size={18} />}
        </button>
      </div>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-zinc-800 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {parcoursSteps.length > 0 ? (
            <div className="rounded-lg bg-zinc-950/40 p-3 space-y-2" data-testid={`parcours-checklist-${item.ref}`}>
              <div className="text-[11px] text-zinc-500 uppercase tracking-wide mb-1">Test étape par étape (coche en direct)</div>
              {parcoursSteps.map((step, i) => {
                const on = checked.includes(i);
                return (
                  <button key={i} onClick={() => toggleStep(i)} data-testid={`parcours-step-${item.ref}-${i}`}
                    className={`w-full flex items-center gap-2.5 text-left px-2.5 py-2 rounded-lg border transition-colors ${on ? "bg-emerald-500/10 border-emerald-500/40" : "bg-zinc-900 border-zinc-800 hover:border-zinc-700"}`}>
                    <span className={`shrink-0 w-5 h-5 rounded-md border flex items-center justify-center ${on ? "bg-emerald-500 border-emerald-500" : "border-zinc-600"}`}>
                      {on && <CheckCircle size={14} weight="fill" className="text-white" />}
                    </span>
                    <span className={`text-xs ${on ? "text-emerald-200 line-through" : "text-zinc-300"}`}>{step}</span>
                  </button>
                );
              })}
              {item.fields?.confirmation && <div className="text-[11px] text-zinc-500 pt-1 border-t border-zinc-800">🎯 Confirmation finale : {item.fields.confirmation}</div>}
            </div>
          ) : (
            <div className="rounded-lg bg-zinc-950/40 p-3"><CategoryFields item={item} /></div>
          )}
          <div className="space-y-2">
            <div>
              <label className="text-[11px] text-zinc-500 flex items-center gap-1 mb-1"><WarningCircle size={12} /> Problème détecté</label>
              <textarea data-testid={`audit-probleme-${item.ref}`} rows={2} value={draft.probleme_detecte}
                onChange={(e) => setDraft((d) => ({ ...d, probleme_detecte: e.target.value }))} className={ta} placeholder="Ce qui bloque ou casse…" />
            </div>
            <div>
              <label className="text-[11px] text-zinc-500 mb-1 block">Correction à faire</label>
              <textarea data-testid={`audit-correction-${item.ref}`} rows={2} value={draft.correction}
                onChange={(e) => setDraft((d) => ({ ...d, correction: e.target.value }))} className={ta} placeholder="Action de correction…" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input data-testid={`audit-resp-${item.ref}`} value={draft.responsable} onChange={(e) => setDraft((d) => ({ ...d, responsable: e.target.value }))}
                className={ta} placeholder="Responsable" />
              <input data-testid={`audit-date-${item.ref}`} value={draft.date_cible} onChange={(e) => setDraft((d) => ({ ...d, date_cible: e.target.value }))}
                className={ta} placeholder="Date cible (ex. Avant rentrée)" />
            </div>
            <textarea rows={1} value={draft.commentaires} onChange={(e) => setDraft((d) => ({ ...d, commentaires: e.target.value }))}
              className={ta} placeholder="Commentaires" />
            <button data-testid={`audit-save-${item.ref}`} onClick={() => save()} disabled={!dirty || saving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-500 disabled:opacity-40 disabled:cursor-not-allowed text-amber-50 text-xs font-medium">
              {saving ? <CircleNotch size={14} className="animate-spin" /> : <FloppyDisk size={14} />} Enregistrer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AuditRentreeModule() {
  const [data, setData] = useState(null);
  const [overview, setOverview] = useState(null);
  const [guides, setGuides] = useState([]);
  const [activation, setActivation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("app");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [it, ov, gd, ac] = await Promise.all([
        api.get("/audit-rentree/items"), api.get("/audit-rentree/overview"),
        api.get("/audit-rentree/guides"), api.get("/audit-rentree/activation-status"),
      ]);
      setData(it.data); setOverview(ov.data);
      setGuides(gd.data.guides || []); setActivation(ac.data || null);
    } catch { toast.error("Impossible de charger l'audit."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSaved = (ref, payload) => {
    setData((d) => ({ ...d, items: d.items.map((x) => x.ref === ref ? { ...x, ...payload } : x) }));
    // Recalcule l'aperçu localement
    setTimeout(() => { api.get("/audit-rentree/overview").then((r) => setOverview(r.data)).catch(() => {}); }, 300);
  };

  const saveGuideVideo = async (role, url) => {
    try {
      await api.put(`/audit-rentree/guides/${encodeURIComponent(role)}/video`, { video_url: url });
      setGuides((gs) => gs.map((g) => g.role === role ? { ...g, video_url: url } : g));
      toast.success(`Vidéo du guide ${role} enregistrée.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec."); }
  };
  const createCarnet = async (contractId) => {
    try {
      const { data: r } = await api.post(`/audit-rentree/create-carnet/${contractId}`);
      toast.success(r.already ? "Ce carnet existait déjà." : `Carnet créé (${r.techniques_attached || 0} techniques rattachées).`);
      const ac = await api.get("/audit-rentree/activation-status");
      setActivation(ac.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de la création du carnet."); }
  };

  if (loading) return <div className="flex items-center justify-center py-24 text-zinc-500"><CircleNotch size={30} className="animate-spin" /></div>;
  if (!data || !overview) return null;

  const items = data.items.filter((i) => i.category === tab);
  const statuts = data.statuts; const priorites = data.priorites; const catLabels = data.category_labels;

  return (
    <div className="space-y-6" data-testid="audit-rentree-module">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-amber-500/15 flex items-center justify-center"><ClipboardText size={24} className="text-amber-400" /></div>
        <div>
          <h1 className="text-2xl font-bold text-zinc-100">Audit &amp; Rentrée</h1>
          <p className="text-sm text-zinc-400">Tableau maître : application + Stripe + parcours. Vérifie, corrige, prépare la rentrée.</p>
        </div>
      </div>

      {/* Vue d'ensemble */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 space-y-3" data-testid="audit-overview">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-zinc-200">Progression globale (prêt rentrée)</span>
          <div className="flex items-center gap-3">
            {overview.by_status?.fonctionnel > 0 && <button data-testid="audit-mark-all-ready" onClick={async () => { if (!window.confirm(`Passer les ${overview.by_status.fonctionnel} points « Fonctionnel » à « Prêt rentrée » ?`)) return; try { const r = await api.post("/audit-rentree/items/mark-all-ready"); toast.success(`${r.data.updated} point(s) passés à Prêt rentrée`); load(); } catch { toast.error("Échec"); } }}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white">✔ Tout passer à « Prêt rentrée »</button>}
            <span className="text-sm font-bold text-amber-400" data-testid="audit-progress">{overview.progress}% · {overview.done}/{overview.total}</span>
          </div>
        </div>
        <div className="h-2.5 rounded-full bg-zinc-800 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-amber-500 to-emerald-500 transition-all" style={{ width: `${overview.progress}%` }} />
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          {Object.entries(statuts).map(([k, v]) => (
            <span key={k} className={`text-[11px] px-2 py-1 rounded-lg border ${STATUT_STYLE[k]}`}>{v} : {overview.by_status[k] || 0}</span>
          ))}
        </div>
      </div>

      {/* Onglets par catégorie */}
      <div className="flex flex-wrap gap-2">
        {CAT_ORDER.map((c) => {
          const Icon = CAT_ICON[c]; const cat = overview.by_category[c] || { total: 0, done: 0 };
          return (
            <button key={c} data-testid={`audit-tab-${c}`} onClick={() => setTab(c)}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${tab === c ? "bg-amber-600 text-amber-50" : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800 border border-zinc-800"}`}>
              <Icon size={16} /> {catLabels[c]}
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${tab === c ? "bg-amber-700/60" : "bg-zinc-800"}`}>{cat.done}/{cat.total}</span>
            </button>
          );
        })}
        <button data-testid="audit-tab-guides" onClick={() => setTab("guides")}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "guides" ? "bg-sky-600 text-sky-50" : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800 border border-zinc-800"}`}>
          <BookOpen size={16} /> Guides de rentrée
        </button>
        <button data-testid="audit-tab-activation" onClick={() => setTab("activation")}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "activation" ? "bg-emerald-600 text-emerald-50" : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800 border border-zinc-800"}`}>
          <IdentificationCard size={16} /> Activation membres
          {activation && activation.sans_carnet + activation.sans_discipline > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/30 text-red-200">{activation.sans_carnet + activation.sans_discipline}</span>
          )}
        </button>
      </div>

      {/* Contenu */}
      {tab === "guides" ? (
        <GuidesView guides={guides} onSaveVideo={saveGuideVideo} />
      ) : tab === "activation" ? (
        <ActivationView activation={activation} onCreateCarnet={createCarnet} onRefresh={async () => { const ac = await api.get("/audit-rentree/activation-status"); setActivation(ac.data); }} />
      ) : (
        <div className="space-y-2.5">
          {items.map((it) => <AuditCard key={it.ref} item={it} statuts={statuts} priorites={priorites} onSaved={onSaved} />)}
        </div>
      )}
    </div>
  );
}

const GUIDE_ICON = { user: User, baby: Baby, whistle: Barbell, shield: ShieldCheck };

function GuidesView({ guides, onSaveVideo }) {
  return (
    <div className="space-y-4" data-testid="guides-view">
      <div className="flex items-center justify-between">
        <p className="text-xs text-zinc-500">Guides pas-à-pas par rôle. Colle un lien vidéo YouTube pour accompagner chaque guide. Utilise « Imprimer » pour un PDF.</p>
        <button data-testid="print-guides-btn" onClick={() => window.print()}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-medium"><Printer size={14} /> Imprimer</button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {guides.map((g) => <GuideCard key={g.role} g={g} onSaveVideo={onSaveVideo} />)}
      </div>
    </div>
  );
}

function GuideCard({ g, onSaveVideo }) {
  const Icon = GUIDE_ICON[g.icon] || User;
  const [url, setUrl] = useState(g.video_url || "");
  const dirty = url !== (g.video_url || "");
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 space-y-3" data-testid={`guide-${g.role}`}>
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-lg bg-sky-500/15 flex items-center justify-center"><Icon size={20} className="text-sky-400" /></div>
        <div>
          <div className="text-sm font-bold text-zinc-100">{g.role}</div>
          <div className="text-[11px] text-zinc-500">{g.objectif}</div>
        </div>
      </div>
      <ol className="space-y-2">
        {g.steps.map((s, i) => (
          <li key={i} className="flex gap-2.5">
            <span className="shrink-0 w-5 h-5 rounded-full bg-sky-600/30 text-sky-300 text-[11px] font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
            <div className="min-w-0">
              <div className="text-xs font-medium text-zinc-200">{s.titre}</div>
              <div className="text-[11px] text-zinc-500">{s.detail}</div>
            </div>
          </li>
        ))}
      </ol>
      <div className="border-t border-zinc-800 pt-2 space-y-1.5">
        <label className="text-[10px] text-zinc-500 uppercase tracking-wide">Vidéo (lien YouTube)</label>
        <div className="flex gap-2">
          <input data-testid={`guide-video-input-${g.role}`} value={url} onChange={(e) => setUrl(e.target.value)}
            className="flex-1 rounded-lg border border-zinc-800 bg-zinc-900 px-2.5 py-1.5 text-xs text-zinc-100 outline-none placeholder:text-zinc-600"
            placeholder="https://youtu.be/…" />
          <button data-testid={`guide-video-save-${g.role}`} onClick={() => onSaveVideo(g.role, url.trim())} disabled={!dirty}
            className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 disabled:opacity-40 disabled:cursor-not-allowed text-sky-50 text-xs font-medium"><FloppyDisk size={13} /></button>
        </div>
        {g.video_url && (
          <a href={g.video_url} target="_blank" rel="noreferrer" data-testid={`guide-video-link-${g.role}`}
            className="text-[11px] text-sky-400 hover:text-sky-300 inline-flex items-center gap-1">▶ Voir la vidéo</a>
        )}
      </div>
      <div className="text-[10px] text-zinc-600">Format suggéré : {g.format}</div>
    </div>
  );
}

function ActivationView({ activation, onCreateCarnet, onRefresh }) {
  const [busy, setBusy] = useState(null);
  const [pick, setPick] = useState({});
  if (!activation) return null;
  const handleCreate = async (id) => { setBusy(id); await onCreateCarnet(id); setBusy(null); };
  const disciplines = activation.disciplines || [];
  const assign = async (id) => {
    const discipline = pick[id];
    if (!discipline) { toast.error("Choisis une discipline."); return; }
    setBusy(id);
    try {
      await api.post(`/audit-rentree/assign-discipline/${id}`, { discipline });
      toast.success("Discipline attribuée.");
      await onRefresh?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec."); }
    setBusy(null);
  };
  const Stat = ({ label, value, tone }) => (
    <div className={`rounded-xl border p-3 text-center ${tone}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-[11px] opacity-80">{label}</div>
    </div>
  );
  return (
    <div className="space-y-4" data-testid="activation-view">
      <p className="text-xs text-zinc-500">Chaîne d'activation « paiement → groupe → carnet ». Crée le carnet manquant en 1 clic (bouton à droite).</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Membres actifs" value={activation.total} tone="border-zinc-800 bg-zinc-900/40 text-zinc-200" />
        <Stat label="Complets (groupe + carnet)" value={activation.complets} tone="border-emerald-500/30 bg-emerald-500/10 text-emerald-300" />
        <Stat label="Sans discipline / groupe" value={activation.sans_discipline} tone="border-orange-500/30 bg-orange-500/10 text-orange-300" />
        <Stat label="Sans carnet" value={activation.sans_carnet} tone="border-red-500/30 bg-red-500/10 text-red-300" />
      </div>
      <div className="rounded-xl border border-zinc-800 overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-zinc-900/60 text-[11px] uppercase tracking-wide text-zinc-500">
          <div className="col-span-4">Membre</div><div className="col-span-4">Discipline / groupe</div>
          <div className="col-span-4 text-right">Carnet</div>
        </div>
        {activation.members.map((m) => (
          <div key={m.id} className="grid grid-cols-12 gap-2 px-4 py-2.5 border-t border-zinc-800 text-xs items-center hover:bg-zinc-900/40" data-testid={`activation-row-${m.id}`}>
            <div className="col-span-4 truncate text-zinc-200 flex items-center gap-1.5">
              {m.complet ? <CheckCircle size={14} weight="fill" className="text-emerald-400 shrink-0" /> : <WarningCircle size={14} className="text-orange-400 shrink-0" />}
              {m.full_name}
            </div>
            <div className="col-span-4 truncate">
              {m.a_discipline ? <span className="text-zinc-300">{m.discipline}</span> : (
                <span className="inline-flex items-center gap-1.5">
                  <select value={pick[m.id] || ""} onChange={(e) => setPick({ ...pick, [m.id]: e.target.value })} data-testid={`assign-discipline-select-${m.id}`}
                    className="bg-black/40 border border-orange-500/40 rounded px-1.5 py-1 text-[11px] text-zinc-200 focus:outline-none">
                    <option value="">Choisir…</option>
                    {disciplines.map((d) => <option key={d.key} value={d.key}>{d.nom}</option>)}
                  </select>
                  <button onClick={() => assign(m.id)} disabled={busy === m.id} data-testid={`assign-discipline-btn-${m.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-orange-500/80 hover:bg-orange-400 text-orange-50 text-[11px] font-medium disabled:opacity-50">
                    {busy === m.id ? <CircleNotch size={11} className="animate-spin" /> : "Assigner"}
                  </button>
                </span>
              )}
            </div>
            <div className="col-span-4 text-right">
              {m.a_carnet ? (
                <span className="text-emerald-400 inline-flex items-center gap-1"><CheckCircle size={13} /> OK</span>
              ) : m.a_discipline ? (
                <button data-testid={`create-carnet-${m.id}`} onClick={() => handleCreate(m.id)} disabled={busy === m.id}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-red-600/80 hover:bg-red-500 text-red-50 text-[11px] font-medium disabled:opacity-50">
                  {busy === m.id ? <CircleNotch size={12} className="animate-spin" /> : <IdentificationCard size={12} />} Créer le carnet
                </button>
              ) : (
                <span className="text-zinc-500 text-[11px] italic">Attribue une discipline d'abord</span>
              )}
            </div>
          </div>
        ))}
        {activation.members.length === 0 && <div className="px-4 py-8 text-center text-sm text-zinc-500">Aucun membre actif.</div>}
      </div>
    </div>
  );
}
